"use client";

import {
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock3,
  CornerUpLeft,
  MessageSquare,
  RotateCcw,
  UserRound,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefCallback,
} from "react";
import { AssigneePicker } from "@/components/issues/Pickers";
import { Avatar } from "@/components/ui/Avatar";
import { Modal } from "@/components/ui/Modal";
import { Popover } from "@/components/ui/Popover";
import { PriorityIcon } from "@/components/ui/PriorityIcon";
import { StatusIcon } from "@/components/ui/StatusIcon";
import {
  attentionSnoozeChoices,
  partitionAttentionQueue,
  type AttentionQueueMode,
  type AttentionSnoozeState,
} from "@/lib/attentionQueue";
import { useStore } from "@/lib/store";
import type { Issue, Project, User } from "@/lib/types";
import { cn, formatDate, formatRelative } from "@/lib/utils";
import { humanOwnerId } from "@/lib/taskOwnership";

type Awaitable<T> = T | Promise<T>;
type CommentAction = "reply" | "request-changes";

export interface AttentionQueueViewProps {
  issues: Issue[];
  canWrite: boolean;
  mode: AttentionQueueMode;
  currentUserId: string | null;
  onOpen: (issueId: string) => void;
  onUpdate: (issueId: string, patch: Partial<Issue>) => Awaitable<void>;
  onComment: (issueId: string, body: string) => Awaitable<void>;
  snoozedUntilByIssueId: AttentionSnoozeState;
  onSnooze: (issueId: string, until: number | null) => Awaitable<void>;
  className?: string;
}

export function AttentionQueueView({
  issues,
  canWrite,
  mode,
  currentUserId,
  onOpen,
  onUpdate,
  onComment,
  snoozedUntilByIssueId,
  onSnooze,
  className,
}: AttentionQueueViewProps) {
  const users = useStore((state) => state.users);
  const projects = useStore((state) => state.projects);
  const overlayOpen = useStore(
    (state) =>
      Boolean(state.selectedIssueId) || state.commandOpen || state.createOpen
  );
  const [now, setNow] = useState(() => Date.now());
  const [showSnoozed, setShowSnoozed] = useState(false);
  const [activeIssueId, setActiveIssueId] = useState<string | null>(null);
  const [commentRequest, setCommentRequest] = useState<{
    action: CommentAction;
    issue: Issue;
  } | null>(null);
  const rowRefs = useRef(new Map<string, HTMLElement>());
  const keyboardNavigation = useRef(false);

  const partition = useMemo(
    () =>
      partitionAttentionQueue({
        issues,
        mode,
        currentUserId,
        snoozedUntilByIssueId,
        now,
      }),
    [currentUserId, issues, mode, now, snoozedUntilByIssueId]
  );
  const keyboardIssues = useMemo(
    () =>
      showSnoozed
        ? [...partition.active, ...partition.snoozed]
        : partition.active,
    [partition.active, partition.snoozed, showSnoozed]
  );

  // Wake the queue when its nearest snooze expires instead of requiring a
  // reload. Focus/visibility hooks cover laptops that slept through the timer.
  useEffect(() => {
    const nextSnoozeAt = Object.values(snoozedUntilByIssueId)
      .filter((value): value is number => value != null && value > now)
      .sort((a, b) => a - b)[0];
    const refresh = () => setNow(Date.now());
    const delay = nextSnoozeAt
      ? Math.min(Math.max(nextSnoozeAt - now + 50, 50), 2_147_000_000)
      : null;
    const timer = delay == null ? null : window.setTimeout(refresh, delay);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      if (timer != null) window.clearTimeout(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [now, snoozedUntilByIssueId]);

  useEffect(() => {
    if (keyboardIssues.length === 0) {
      setActiveIssueId(null);
      return;
    }
    if (!keyboardIssues.some((issue) => issue.id === activeIssueId)) {
      setActiveIssueId(keyboardIssues[0].id);
    }
  }, [activeIssueId, keyboardIssues]);

  useEffect(() => {
    if (!activeIssueId) return;
    const row = rowRefs.current.get(activeIssueId);
    row?.scrollIntoView({
      block: "nearest",
    });
    if (keyboardNavigation.current) {
      row?.focus({ preventScroll: true });
      keyboardNavigation.current = false;
    }
  }, [activeIssueId]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (commentRequest || overlayOpen || event.defaultPrevented) return;
      const target = event.target as HTMLElement | null;
      const typing =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.tagName === "SELECT" ||
        target?.isContentEditable;
      const interactive = target?.closest(
        "button, a, [role='button'], [role='menuitem'], [role='option']"
      );
      if (
        typing ||
        interactive ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey
      ) {
        return;
      }

      const key = event.key.toLowerCase();
      if (key !== "j" && key !== "k" && event.key !== "Enter") return;
      if (keyboardIssues.length === 0) return;
      event.preventDefault();

      const currentIndex = keyboardIssues.findIndex(
        (issue) => issue.id === activeIssueId
      );
      if (event.key === "Enter") {
        const issue = keyboardIssues[Math.max(currentIndex, 0)];
        if (issue) onOpen(issue.id);
        return;
      }

      const direction = key === "j" ? 1 : -1;
      const startIndex =
        currentIndex === -1
          ? direction === 1
            ? -1
            : keyboardIssues.length
          : currentIndex;
      const nextIndex =
        (startIndex + direction + keyboardIssues.length) %
        keyboardIssues.length;
      keyboardNavigation.current = true;
      setActiveIssueId(keyboardIssues[nextIndex].id);
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeIssueId, commentRequest, keyboardIssues, onOpen, overlayOpen]);

  const registerRow =
    (issueId: string): RefCallback<HTMLElement> =>
    (element) => {
      if (element) rowRefs.current.set(issueId, element);
      else rowRefs.current.delete(issueId);
    };

  const emptyTitle =
    mode === "awaiting" ? "Nothing awaiting your review" : "Your inbox is clear";
  const emptyBody =
    partition.snoozed.length > 0
      ? "Everything else is snoozed for later."
      : mode === "awaiting"
        ? "Work sent back for your sign-off will appear here."
        : "Open tasks assigned to you will appear here.";

  return (
    <div
      className={cn(
        "flex min-h-0 flex-1 flex-col bg-[var(--bg)]",
        className
      )}
      role="region"
      aria-label={mode === "awaiting" ? "Awaiting review queue" : "Inbox queue"}
    >
      <div className="flex shrink-0 items-center gap-3 border-b border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-2 md:px-5">
        <span
          aria-live="polite"
          className="text-[11.5px] text-[var(--fg-tertiary)]"
        >
          {partition.active.length}{" "}
          {partition.active.length === 1 ? "item" : "items"} needing you
        </span>
        <div className="ml-auto hidden items-center gap-1.5 text-[10.5px] text-[var(--fg-muted)] md:flex">
          <kbd className="kbd">J</kbd>
          <kbd className="kbd">K</kbd>
          <span>navigate</span>
          <kbd className="kbd ml-1">↵</kbd>
          <span>open</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {partition.active.length === 0 ? (
          <div className="flex min-h-[280px] items-center justify-center px-6 py-12 text-center">
            <div className="max-w-[360px]">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--bg-tertiary)]">
                <CheckCircle2 size={19} className="text-[var(--green)]" />
              </div>
              <h2 className="mt-4 text-[15.5px] font-medium tracking-tight text-[var(--fg)]">
                {emptyTitle}
              </h2>
              <p className="mt-1 text-[12.5px] leading-5 text-[var(--fg-tertiary)]">
                {emptyBody}
              </p>
            </div>
          </div>
        ) : (
          <div className="mx-auto w-full max-w-[980px] px-3 py-3 md:px-5 md:py-4">
            <div className="space-y-2">
              {partition.active.map((issue) => (
                <AttentionQueueRow
                  key={issue.id}
                  rowRef={registerRow(issue.id)}
                  issue={issue}
                  mode={mode}
                  active={issue.id === activeIssueId}
                  canWrite={canWrite}
                  assignee={users.find(
                    (user) =>
                      user.id === humanOwnerId(issue)
                  )}
                  project={projects.find(
                    (project) => project.id === issue.projectId
                  )}
                  onActivate={() => setActiveIssueId(issue.id)}
                  onOpen={() => onOpen(issue.id)}
                  onUpdate={(patch) => onUpdate(issue.id, patch)}
                  onComment={(action) =>
                    setCommentRequest({ action, issue })
                  }
                  onSnooze={(until) => onSnooze(issue.id, until)}
                />
              ))}
            </div>
          </div>
        )}

        {partition.snoozed.length > 0 && (
          <div className="mx-auto w-full max-w-[980px] px-3 pb-4 md:px-5">
            <button
              type="button"
              onClick={() => setShowSnoozed((value) => !value)}
              className="focus-ring flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-[11.5px] font-medium text-[var(--fg-tertiary)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg-secondary)]"
              aria-expanded={showSnoozed}
              aria-controls={`${mode}-snoozed-items`}
            >
              {showSnoozed ? (
                <ChevronDown size={12} />
              ) : (
                <ChevronRight size={12} />
              )}
              <Clock3 size={12} />
              Snoozed
              <span className="text-[var(--fg-muted)]">
                {partition.snoozed.length}
              </span>
            </button>

            {showSnoozed && (
              <div
                id={`${mode}-snoozed-items`}
                className="mt-1 space-y-2"
              >
                {partition.snoozed.map((issue) => (
                  <AttentionQueueRow
                    key={issue.id}
                    rowRef={registerRow(issue.id)}
                    issue={issue}
                    mode={mode}
                    active={issue.id === activeIssueId}
                    canWrite={canWrite}
                    assignee={users.find(
                      (user) =>
                        user.id === humanOwnerId(issue)
                    )}
                    project={projects.find(
                      (project) => project.id === issue.projectId
                    )}
                    snoozedUntil={snoozedUntilByIssueId[issue.id] ?? null}
                    onActivate={() => setActiveIssueId(issue.id)}
                    onOpen={() => onOpen(issue.id)}
                    onUpdate={(patch) => onUpdate(issue.id, patch)}
                    onComment={(action) =>
                      setCommentRequest({ action, issue })
                    }
                    onSnooze={(until) => onSnooze(issue.id, until)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {commentRequest && (
        <AttentionCommentDialog
          key={`${commentRequest.issue.id}:${commentRequest.action}`}
          request={commentRequest}
          onClose={() => setCommentRequest(null)}
          onSubmit={async (body) => {
            await onComment(commentRequest.issue.id, body);
            if (commentRequest.action === "request-changes") {
              await onUpdate(commentRequest.issue.id, {
                status: "in_progress",
              });
            }
            setCommentRequest(null);
          }}
        />
      )}
    </div>
  );
}

function AttentionQueueRow({
  rowRef,
  issue,
  mode,
  active,
  canWrite,
  assignee,
  project,
  snoozedUntil = null,
  onActivate,
  onOpen,
  onUpdate,
  onComment,
  onSnooze,
}: {
  rowRef: RefCallback<HTMLElement>;
  issue: Issue;
  mode: AttentionQueueMode;
  active: boolean;
  canWrite: boolean;
  assignee?: User;
  project?: Project;
  snoozedUntil?: number | null;
  onActivate: () => void;
  onOpen: () => void;
  onUpdate: (patch: Partial<Issue>) => Awaitable<void>;
  onComment: (action: CommentAction) => void;
  onSnooze: (until: number | null) => Awaitable<void>;
}) {
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const overdue = issue.dueDate != null && issue.dueDate < Date.now();

  async function runAction(name: string, action: () => Awaitable<void>) {
    if (busyAction) return;
    setBusyAction(name);
    setError(null);
    try {
      await action();
    } catch {
      setError("Pauta could not apply that change. Try again.");
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <article
      ref={rowRef}
      data-attention-issue={issue.id}
      role="group"
      aria-label={`${issue.identifier}: ${issue.title}`}
      aria-current={active ? "true" : undefined}
      tabIndex={active ? 0 : -1}
      onFocus={onActivate}
      onMouseEnter={onActivate}
      className={cn(
        "focus-ring group rounded-lg border bg-[var(--bg-secondary)] transition-colors",
        active
          ? "border-[var(--border-strong)] bg-[var(--bg-hover)]"
          : "border-[var(--border)] hover:border-[var(--border-strong)] hover:bg-[var(--bg-hover)]"
      )}
    >
      <div className="px-3 py-3 md:px-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex shrink-0 items-center gap-2">
            <PriorityIcon priority={issue.priority} size={14} />
            <StatusIcon status={issue.status} size={14} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-start gap-2">
              <button
                type="button"
                onFocus={onActivate}
                onClick={onOpen}
                className="focus-ring min-w-0 flex-1 rounded text-left"
              >
                <span className="mr-2 font-mono text-[11px] text-[var(--fg-muted)]">
                  {issue.identifier}
                </span>
                <span className="text-[13px] font-medium leading-5 text-[var(--fg)]">
                  {issue.title}
                </span>
              </button>
              <Avatar user={assignee ?? null} size={20} />
            </div>

            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10.5px] text-[var(--fg-muted)]">
              <span className="text-[var(--fg-tertiary)]">
                {mode === "awaiting"
                  ? "Ready for your review"
                  : `Updated ${formatRelative(issue.updatedAt)}`}
              </span>
              {project && (
                <span className="inline-flex min-w-0 items-center gap-1">
                  <span>{project.icon}</span>
                  <span className="max-w-[180px] truncate">{project.name}</span>
                </span>
              )}
              {issue.dueDate && (
                <span
                  className={cn(
                    "inline-flex items-center gap-1",
                    overdue && "text-[var(--red)]"
                  )}
                >
                  <Clock3 size={10} />
                  {overdue ? "Overdue" : "Due"} {formatDate(issue.dueDate)}
                </span>
              )}
              {snoozedUntil && (
                <span className="inline-flex items-center gap-1 text-[var(--purple)]">
                  <Clock3 size={10} />
                  Snoozed until {formatSnoozeTime(snoozedUntil)}
                </span>
              )}
            </div>

            {canWrite && (
              <div
                className={cn(
                  "mt-3 flex flex-wrap items-center gap-1.5 transition-opacity",
                  active
                    ? "sm:opacity-100"
                    : "sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
                )}
              >
                {mode === "awaiting" ? (
                  <>
                    <ActionButton
                      label="Approve"
                      icon={<Check size={12} />}
                      disabled={busyAction != null}
                      emphasis="success"
                      onClick={() =>
                        void runAction("approve", () =>
                          onUpdate({ status: "done" })
                        )
                      }
                    />
                    <ActionButton
                      label="Request changes"
                      icon={<CornerUpLeft size={12} />}
                      disabled={busyAction != null}
                      onClick={() => onComment("request-changes")}
                    />
                  </>
                ) : (
                  <ActionButton
                    label="Mark done"
                    icon={<Check size={12} />}
                    disabled={busyAction != null}
                    emphasis="success"
                    onClick={() =>
                      void runAction("complete", () =>
                        onUpdate({ status: "done" })
                      )
                    }
                  />
                )}

                <ActionButton
                  label="Reply"
                  compact
                  icon={<MessageSquare size={12} />}
                  disabled={busyAction != null}
                  onClick={() => onComment("reply")}
                />

                <AssigneePicker
                  kind="human"
                  value={humanOwnerId(issue)}
                  onChange={(ownerId) =>
                    void runAction("reassign", () => onUpdate({ ownerId }))
                  }
                  trigger={
                    <ActionButton
                      label="Reassign"
                      compact
                      icon={<UserRound size={12} />}
                      disabled={busyAction != null}
                    />
                  }
                />

                <SnoozeMenu
                  disabled={busyAction != null}
                  snoozedUntil={snoozedUntil}
                  onChange={(until) =>
                    void runAction("snooze", () => onSnooze(until))
                  }
                />

                {busyAction && (
                  <span
                    role="status"
                    aria-live="polite"
                    className="ml-1 text-[10.5px] text-[var(--fg-muted)]"
                  >
                    Saving…
                  </span>
                )}
              </div>
            )}

            {error && (
              <p role="alert" className="mt-2 text-[11px] text-[var(--red)]">
                {error}
              </p>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function ActionButton({
  label,
  icon,
  disabled,
  compact = false,
  emphasis = "default",
  onClick,
}: {
  label: string;
  icon: ReactNode;
  disabled?: boolean;
  compact?: boolean;
  emphasis?: "default" | "success";
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        "focus-ring inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-[11.5px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40",
        emphasis === "success"
          ? "border-[color-mix(in_srgb,var(--green)_38%,var(--border))] bg-[color-mix(in_srgb,var(--green)_7%,var(--bg-secondary))] text-[var(--green)] hover:bg-[var(--bg-hover)]"
          : "border-[var(--border)] bg-[var(--bg-secondary)] text-[var(--fg-secondary)] hover:border-[var(--border-strong)] hover:bg-[var(--bg-hover)]",
        compact && "px-2"
      )}
    >
      {icon}
      <span className={compact ? "sr-only lg:not-sr-only" : undefined}>
        {label}
      </span>
    </button>
  );
}

function SnoozeMenu({
  disabled,
  snoozedUntil,
  onChange,
}: {
  disabled: boolean;
  snoozedUntil: number | null;
  onChange: (until: number | null) => void;
}) {
  return (
    <Popover
      align="start"
      width={220}
      trigger={
        <ActionButton
          label={snoozedUntil ? "Change snooze" : "Snooze"}
          compact
          icon={<Clock3 size={12} />}
          disabled={disabled}
        />
      }
    >
      {(close) => (
        <div className="p-1.5">
          <div className="px-2 pb-1.5 pt-1 text-[10.5px] font-medium uppercase tracking-wider text-[var(--fg-muted)]">
            Remind me
          </div>
          {attentionSnoozeChoices().map((choice) => (
            <button
              key={choice.id}
              type="button"
              onClick={() => {
                onChange(choice.until);
                close();
              }}
              className="focus-ring flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-[var(--fg-secondary)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]"
            >
              <Clock3 size={11} className="text-[var(--fg-muted)]" />
              <span>{choice.label}</span>
              <span className="ml-auto text-[10.5px] text-[var(--fg-muted)]">
                {formatSnoozeTime(choice.until)}
              </span>
            </button>
          ))}
          {snoozedUntil && (
            <>
              <div className="my-1 border-t border-[var(--border)]" />
              <button
                type="button"
                onClick={() => {
                  onChange(null);
                  close();
                }}
                className="focus-ring flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-[var(--fg-secondary)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]"
              >
                <RotateCcw size={11} className="text-[var(--fg-muted)]" />
                Return to queue
              </button>
            </>
          )}
        </div>
      )}
    </Popover>
  );
}

function AttentionCommentDialog({
  request,
  onClose,
  onSubmit,
}: {
  request: { action: CommentAction; issue: Issue };
  onClose: () => void;
  onSubmit: (body: string) => Awaitable<void>;
}) {
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestChanges = request.action === "request-changes";

  async function submit() {
    const normalized = body.trim();
    if (!normalized) {
      setError(
        requestChanges
          ? "Explain what needs to change before sending the task back."
          : "Write a reply before posting."
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(normalized);
    } catch {
      setError("Pauta could not post your comment. Try again.");
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      onClose={() => {
        if (!submitting) onClose();
      }}
      width={520}
      ariaLabelledBy="attention-comment-title"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="border-b border-[var(--border)] px-4 py-3">
          <h2
            id="attention-comment-title"
            className="text-[13.5px] font-medium text-[var(--fg)]"
          >
            {requestChanges ? "Request changes" : "Reply to task"}
          </h2>
          <p className="mt-1 truncate text-[11.5px] text-[var(--fg-tertiary)]">
            {request.issue.identifier} · {request.issue.title}
          </p>
        </div>
        <div className="px-4 py-4">
          <label
            htmlFor="attention-comment"
            className="text-[11.5px] font-medium text-[var(--fg-secondary)]"
          >
            {requestChanges ? "What needs to change?" : "Reply"}
          </label>
          <textarea
            id="attention-comment"
            autoFocus
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder={
              requestChanges
                ? "Be specific so the next pass is actionable…"
                : "Add context or a decision…"
            }
            rows={5}
            className="mt-2 w-full resize-none rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-3 py-2 text-[13px] leading-5 text-[var(--fg)] outline-none placeholder:text-[var(--fg-muted)] focus:border-[var(--accent)]"
          />
          {requestChanges && (
            <p className="mt-2 text-[10.5px] text-[var(--fg-muted)]">
              Posting this comment returns the task to In Progress.
            </p>
          )}
          {error && (
            <p role="alert" className="mt-2 text-[11px] text-[var(--red)]">
              {error}
            </p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-[var(--border)] bg-[var(--bg-secondary)] px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="focus-ring rounded-md border border-[var(--border)] px-3 py-1.5 text-[12px] font-medium text-[var(--fg-secondary)] transition-colors hover:bg-[var(--bg-hover)] disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="focus-ring rounded-md bg-[var(--accent)] px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-[var(--accent-hover)] disabled:opacity-40"
          >
            {submitting
              ? "Posting…"
              : requestChanges
                ? "Send back"
                : "Post reply"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function formatSnoozeTime(timestamp: number): string {
  return new Date(timestamp).toLocaleString(undefined, {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}
